import {useAllRawTransactionsLiteQuery} from '@transactions/graphql';
import Money from 'common/Money';
import * as React from 'react';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import Loading from 'src/Loading';
import {
  MIGRATION_FROM_COLOR,
  MIGRATION_TO_COLOR,
} from 'src/MigrationTimeline';
import type {AllRawTransactionsLiteQuery} from '@transactions/graphql';

// Per-side fetch cap. The overlap can span an account's whole history (the
// duplicate-import case this preview exists to catch), and rows render inline
// in the modal's own scroll — a nested scroll region is miserable on touch —
// so the cap bounds both the query and the DOM. Truncation is called out in
// the summary line.
const PREVIEW_LIMIT = 100;

// The lite projection deliberately: BasicRawTransaction's `account` sub-tree
// resolves transactionCount/balanceStaleSince per ROW on the server (a
// MongoDB count each), which at 2 × PREVIEW_LIMIT rows means hundreds of
// redundant queries per expansion. Which account a row belongs to is already
// known here from which side's query returned it.
type LiteRawTransaction =
  AllRawTransactionsLiteQuery['allRawTransactions']['items'][number];

type Side = 'from' | 'to';

type SidedTransaction = {
  side: Side;
  transaction: LiteRawTransaction;
};

// Newest first (matching every other transaction list), then by amount and
// description within a day — so a duplicated import (same date, same amount,
// one row from each account) lands as adjacent rows the eye can pair up.
const compareForPairing = (a: SidedTransaction, b: SidedTransaction): number => {
  const ta = a.transaction;
  const tb = b.transaction;
  if (ta.date !== tb.date) {
    return ta.date < tb.date ? 1 : -1;
  }
  if (ta.amount !== tb.amount) {
    return ta.amount - tb.amount;
  }
  if (ta.description !== tb.description) {
    return ta.description < tb.description ? -1 : 1;
  }
  return ta._id < tb._id ? -1 : 1;
};

const sideColor = (side: Side): string =>
  side === 'from' ? MIGRATION_FROM_COLOR : MIGRATION_TO_COLOR;

const SideDot = ({side}: {side: Side}) => (
  <span
    aria-hidden
    className="inline-block size-2 shrink-0 rounded-sm"
    style={{backgroundColor: sideColor(side)}}
  />
);

// A compact, non-navigable rendition of RawTransactionRow's layout. The full
// row component requires the BasicRawTransaction fragment (see the projection
// note above), and its extras — long-press, benefit pill, allocation status —
// are all irrelevant to reviewing an overlap.
const OverlapRow = ({
  side,
  accountName,
  transaction,
}: SidedTransaction & {accountName: string}) => (
  <div
    className="grid items-center gap-x-5 gap-y-1.5 border-l-2 px-4 py-3 [grid-template-columns:1fr_auto]"
    style={{borderLeftColor: sideColor(side)}}
  >
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="text-foreground truncate" title={transaction.description}>
        {transaction.description}
      </div>
      <div className="text-muted-foreground truncate text-sm">
        {accountName}
      </div>
    </div>
    <div className="flex min-w-0 flex-col items-end justify-center gap-1 pl-3 text-right">
      <AmountDisplay
        amount={Money.fromInt(transaction.amount, transaction.currency)}
      />
      <div className="text-muted-foreground tabular-nums">
        {transaction.date}
      </div>
    </div>
  </div>
);

type Props = {
  fromAccountId: string;
  fromAccountName: string;
  toAccountId: string;
  toAccountName: string;
  /** ISO dates bounding the overlap window (inclusive on both ends). */
  overlapStartDate: string;
  overlapEndDate: string;
};

/**
 * On-demand list of the transactions inside a migration's overlap window,
 * from both accounts merged into one date-ordered list, each row edged with
 * the timeline's color for its side. The overlap warning asks the user to
 * attest they reviewed the overlap; this is what lets them actually do that
 * without abandoning the modal. Collapsed by default (overlap can be
 * legitimate, and the warning should stay scannable); rows load only on
 * expand.
 */
export const MigrationOverlapPreview = ({
  fromAccountId,
  fromAccountName,
  toAccountId,
  toAccountName,
  overlapStartDate,
  overlapEndDate,
}: Props) => {
  const [expanded, setExpanded] = React.useState(false);

  // network-only for the same reason as the migration preview itself: this
  // informs an irreversible action, so show current server state.
  const commonOptions = {
    skip: !expanded,
    fetchPolicy: 'network-only' as const,
  };
  const commonVariables = {
    includeProcessed: true,
    limit: PREVIEW_LIMIT,
    startDate: overlapStartDate,
    endDate: overlapEndDate,
  };
  const fromQuery = useAllRawTransactionsLiteQuery({
    variables: {...commonVariables, accountId: fromAccountId},
    ...commonOptions,
  });
  const toQuery = useAllRawTransactionsLiteQuery({
    variables: {...commonVariables, accountId: toAccountId},
    ...commonOptions,
  });

  const toggle = (
    <Button
      variant="outline"
      className="self-start px-2.5 py-1 text-xs shadow-none"
      onClick={() => setExpanded(!expanded)}
    >
      {expanded
        ? 'Hide overlapping transactions'
        : 'Show the overlapping transactions'}
    </Button>
  );

  if (!expanded) {
    return toggle;
  }

  const loading = fromQuery.loading || toQuery.loading;
  const error = fromQuery.error ?? toQuery.error;
  const fromPage = fromQuery.data?.allRawTransactions;
  const toPage = toQuery.data?.allRawTransactions;
  const merged: SidedTransaction[] = [
    ...(fromPage?.items ?? []).map((transaction) => ({
      side: 'from' as const,
      transaction,
    })),
    ...(toPage?.items ?? []).map((transaction) => ({
      side: 'to' as const,
      transaction,
    })),
  ].sort(compareForPairing);
  const truncated = fromPage?.hasMore || toPage?.hasMore;

  return (
    <div className="flex flex-col gap-2">
      {toggle}

      {loading && <Loading size="small" />}

      {error && <p className="text-destructive m-0 text-sm">{error.message}</p>}

      {!loading && !error && fromPage && toPage && (
        <>
          <p className="text-foreground m-0 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
            <span className="flex items-center gap-1.5">
              <SideDot side="from" />
              {fromPage.items.length.toLocaleString()} from {fromAccountName}{' '}
              (moving in)
            </span>
            <span aria-hidden>·</span>
            <span className="flex items-center gap-1.5">
              <SideDot side="to" />
              {toPage.items.length.toLocaleString()} in {toAccountName}{' '}
              (already there)
            </span>
          </p>
          {truncated && (
            <p className="text-muted-foreground m-0 text-xs">
              Showing only the most recent {PREVIEW_LIMIT} per account.
            </p>
          )}
          <div className="border-border divide-border bg-background divide-y rounded-md border">
            {merged.map(({side, transaction}) => (
              <OverlapRow
                key={transaction._id}
                side={side}
                transaction={transaction}
                accountName={side === 'from' ? fromAccountName : toAccountName}
              />
            ))}
            {merged.length === 0 && (
              <p className="text-muted-foreground m-0 p-3 text-sm">
                No transactions found in the overlap window.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
